import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  makeStyles,
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableRow,
  Typography
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import Filter, { IFilter } from "@saleor/components/Filter";
import Skeleton from "@saleor/components/Skeleton";
import TableCellHeader from "@saleor/components/TableCellHeader";
import TablePagination from "@saleor/components/TablePagination/TablePagination";
import { DEFAULT_INITIAL_PAGINATION_DATA } from "@saleor/config";
import useListSettings from "@saleor/hooks/useListSettings";
import useNavigator from "@saleor/hooks/useNavigator";
import usePaginator, {
  createPaginationState,
  PaginationState
} from "@saleor/hooks/usePaginator";
import createFilterHandlersWithId from "@saleor/malls/filters/filterHandlers";
import { getFilterOpts } from "@saleor/malls/filters/filterOpts";
import { getFilterQueryParam } from "@saleor/malls/filters/filterQueryParams";
import {
  createFilterStructure,
  MallShippingLogsFilterKeys
} from "@saleor/malls/filters/filters";
import { useMallShippingMethodActivityLogs } from "@saleor/malls/queries";
import { getSortQueryVariables } from "@saleor/malls/sorts/sort";
import { MallShippingLogsSortField, mallUrl } from "@saleor/malls/urls";
import { maybe, renderCollection } from "@saleor/misc";
import { DialogProps, ListViews, SortPage } from "@saleor/types";
import {
  ActivityLogSortField,
  OrderDirection
} from "@saleor/types/globalTypes";
import { getArrowDirection } from "@saleor/utils/sort";
import Moment from "moment";
import React from "react";
import { useIntl } from "react-intl";

const useModalStyles = makeStyles(
  _ => ({
    modal: {
      height: "60vh",
      padding: 0
    },
    modalCloseBtn: {
      position: "absolute",
      right: "15px",
      top: "10px"
    },
    modalTitleText: {
      fontSize: "24px",
      fontWeight: 600
    }
  }),
  { name: "MallDeliveryTimeHistoryModal" }
);

const useTableStyles = makeStyles(
  theme => ({
    colStatus: {
      width: "auto",
      [theme.breakpoints.down("md")]: {
        width: "auto"
      }
    },
    dataDisplay: {},
    dataTitle: {
      display: "none !important"
    },
    noMargin: {
      margin: "0"
    },
    tableCellBody: {
      [theme.breakpoints.down("lg")]: {
        fontSize: "14px"
      }
    },
    tableCellHeader: {
      fontSize: "14px",
      fontWeight: "bold",
      textAlign: "left",
      [theme.breakpoints.down("lg")]: {
        fontSize: "1em"
      }
    },
    tableHeaderTitle: {
      fontSize: "18px",
      fontWeight: 400
    },
    [theme.breakpoints.down("sm")]: {
      dataGroup: {},
      dataTitle: {}
    }
  }),
  { name: "MallDeliveryTimeHistoryTable" }
);

export interface MallDeliveryTimeHistoryProps
  extends DialogProps,
    SortPage<MallShippingLogsSortField> {
  mallId: string;
  params: any;
}

const numberOfColumns = 3;

const MallDeliveryTimeHistory: React.FC<MallDeliveryTimeHistoryProps> = props => {
  const { params, open, onClose, mallId, onSort, sort } = props;

  const tblClasses = useTableStyles(props);
  const classes = useModalStyles(props);
  const navigate = useNavigator();
  const intl = useIntl();
  const paginate = usePaginator();

  const { updateListSettings, settings } = useListSettings(ListViews.MALL_LIST);

  const paginationState =
    createPaginationState(settings.rowNumber, params)?.first !== null
      ? createPaginationState(settings.rowNumber, params)
      : { first: settings.rowNumber };

  const [queryVariables, setQueryVariables] = React.useState<PaginationState>({
    first: settings.rowNumber
  });
  const [search, setSearch] = React.useState(params?.event || "");

  const checkParamsSort = () => {
    if (params.sort === MallShippingLogsSortField.created_date) {
      return getSortQueryVariables(params);
    } else {
      return {
        direction: OrderDirection.DESC,
        field: ActivityLogSortField.CREATED_AT
      };
    }
  };

  const { data, refetch } = useMallShippingMethodActivityLogs({
    displayLoader: true,
    skip: !open && !mallId,
    variables: {
      ...queryVariables,
      search,
      sortBy: checkParamsSort(),
      subject: mallId
    }
  });

  React.useEffect(() => {
    if (open) {
      refetch();
    } else if (!!mallId) {
      resetFilters(mallId);
    }
  }, [open]);

  const { loadNextPage, loadPreviousPage, pageInfo } = paginate(
    data?.mallShippingMethodActivityLogs?.pageInfo,
    paginationState,
    params
  );

  const logs =
    data?.mallShippingMethodActivityLogs?.edges.map(edge => edge.node) || [];

  const filterOpts = getFilterOpts(params);
  const filterMenu = createFilterStructure(intl, filterOpts);

  const [changeFilters, resetFilters] = createFilterHandlersWithId({
    createUrl: mallUrl,
    getFilterQueryParam,
    id: mallId,
    navigate,
    params
  });

  React.useEffect(() => {
    if (mallId) {
      navigate(
        mallUrl(mallId, {
          ...params,
          ...DEFAULT_INITIAL_PAGINATION_DATA
        }),
        true
      );
    }
  }, [mallId, settings.rowNumber]);

  React.useMemo(() => {
    setSearch(params?.event || "");
    setQueryVariables(paginationState);
  }, [params]);

  return (
    <Dialog onClose={onClose} open={open} maxWidth="lg" fullWidth>
      <DialogTitle>
        <IconButton
          aria-label="close"
          onClick={() => onClose()}
          className={classes.modalCloseBtn}
        >
          <CloseIcon />
        </IconButton>
        <Typography className={classes.modalTitleText}>Audit Trail</Typography>
      </DialogTitle>

      <DialogContent className={classes.modal}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCellHeader
                colSpan={numberOfColumns}
                className={`${tblClasses.tableCellHeader} ${tblClasses.colStatus} ${tblClasses.tableHeaderTitle}`}
              >
                <Filter
                  showIcon={true}
                  currencySymbol={""}
                  menu={filterMenu}
                  onFilterAdd={(
                    filter: IFilter<MallShippingLogsFilterKeys>
                  ) => {
                    if (params?.before || params?.after) {
                      delete params.before;
                      delete params.after;
                      navigate(
                        mallUrl(mallId, {
                          ...params,
                          ...DEFAULT_INITIAL_PAGINATION_DATA
                        }),
                        true
                      );
                    }
                    changeFilters(mallId, filter);
                  }}
                />
              </TableCellHeader>
            </TableRow>
            <TableRow>
              <TableCellHeader
                className={`${tblClasses.tableCellHeader} ${tblClasses.colStatus}`}
              >
                Event
              </TableCellHeader>
              <TableCellHeader
                className={`${tblClasses.tableCellHeader} ${tblClasses.colStatus}`}
              >
                Triggered By
              </TableCellHeader>
              <TableCellHeader
                direction={
                  sort.sort === MallShippingLogsSortField.created_date
                    ? getArrowDirection(sort.asc)
                    : undefined
                }
                onClick={() =>
                  onSort(MallShippingLogsSortField.created_date, mallId)
                }
                className={`${tblClasses.tableCellHeader} ${tblClasses.colStatus}`}
              >
                Date & Time
              </TableCellHeader>
            </TableRow>
          </TableHead>
          <TableFooter>
            <TableRow>
              <TablePagination
                colSpan={numberOfColumns}
                settings={settings}
                hasNextPage={pageInfo?.hasNextPage ?? false}
                onNextPage={loadNextPage}
                onUpdateListSettings={updateListSettings}
                hasPreviousPage={pageInfo?.hasPreviousPage ?? false}
                onPreviousPage={loadPreviousPage}
              />
            </TableRow>
          </TableFooter>
          <TableBody>
            {renderCollection(
              logs,
              (log, index) => (
                <TableRow key={index}>
                  <TableCell
                    className={`${tblClasses.tableCellBody} ${tblClasses.colStatus}`}
                  >
                    <div className={tblClasses.dataGroup}>
                      <div className={tblClasses.dataTitle}>Event</div>
                      <div className={tblClasses.dataDisplay}>
                        {maybe(() => log.message) ? (
                          <span>{log.message}</span>
                        ) : (
                          <Skeleton />
                        )}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell
                    className={`${tblClasses.tableCellBody} ${tblClasses.colStatus}`}
                  >
                    <div className={tblClasses.dataGroup}>
                      <div className={tblClasses.dataTitle}>Triggered By</div>
                      <div className={tblClasses.dataDisplay}>
                        {maybe(() => log.doer) ? (
                          <span>
                            <p className={tblClasses.noMargin}>
                              {log.doer?.firstName + " " + log.doer?.lastName}
                            </p>
                            <p className={tblClasses.noMargin}>
                              {log.doer?.email}
                            </p>
                          </span>
                        ) : (
                          <Skeleton />
                        )}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell
                    className={`${tblClasses.tableCellBody} ${tblClasses.colStatus}`}
                  >
                    <div className={tblClasses.dataGroup}>
                      <div className={tblClasses.dataTitle}>Date & Time</div>
                      <div className={tblClasses.dataDisplay}>
                        {maybe(() => log.createdAt) ? (
                          <span>
                            {Moment(log.createdAt).format(
                              "MMMM D YYYY, h:mm a"
                            )}
                          </span>
                        ) : (
                          <Skeleton />
                        )}
                      </div>
                    </div>
                  </TableCell>
                </TableRow>
              ),
              () => (
                <TableRow>
                  <TableCell colSpan={numberOfColumns}>
                    No records found
                  </TableCell>
                </TableRow>
              )
            )}
          </TableBody>
        </Table>
      </DialogContent>
    </Dialog>
  );
};

MallDeliveryTimeHistory.displayName = "MallDeliveryTimeHistory";
export default MallDeliveryTimeHistory;
